import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Box, Divider, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Pagination from '../shared/pagination';
import { CardTable, cardTableContainerSx } from '../shared/card-table';
import { DEFAULT_PAGE_SIZE } from '../shared/utils';
import { globalFilterFn } from '../../../util/filter';
import { Loading } from '../../atomic';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { useStore } from '../../../../hooks/useStore';
import { useListCards } from '../../../../queries/pxtraCard';
import { useCardOverviewColumns } from './columns';
import { HeadlineToolBar } from '../shared/headline-toolbar';

const containerSx = cardTableContainerSx();

export const CardOverviewTable = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';

  const { data, isLoading, isError } = useListCards(companyId);

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  const rows = data?.results ?? [];
  const columns = useCardOverviewColumns();

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    getRowId: (row) => row.employee_id,
    sortDescFirst: false,
    state: { pagination, globalFilter, columnFilters }
  });

  if (isLoading) {
    return (
      <Paper className="p-8">
        <Box className="flex justify-center items-center">
          <Loading />
        </Box>
      </Paper>
    );
  }

  if (isError) {
    return (
      <FeedbackAlert
        severity="error"
        title={t('pxtraCard:loadFailedTitle')}
        description={t('pxtraCard:loadFailedText')}
      />
    );
  }

  return (
    <Paper className="overflow-hidden flex flex-col w-full min-w-0 h-full">
      <HeadlineToolBar table={table} headline="pxtraCard:person" />

      <CardTable
        table={table}
        containerSx={containerSx}
        emptyTitle={t('pxtraCard:cardsEmptyTitle')}
        emptyText={t('pxtraCard:cardsEmptyHelperText')}
      />
      <Divider className="border-grey-100" />
      <Pagination table={table} />
    </Paper>
  );
});
